import { KIT_COMPONENTS } from './kit';
/** At most this many elements per render. */
export const MAX_VIEW_NODES = 10_000;
/**
 * JavaScript that runs inside the sealed engine before the view's code
 * (docs/features/views.md). It gives the view `h`/`Fragment` for JSX, the
 * component names, hooks (useState, useEffect, useMemo, useRef …), `kit`
 * helpers, `setState`/`setParams` and the bridge to Deskworks (useQuery,
 * useWork, useAction, navigate, toast). The engine has no DOM, network,
 * timers or host objects; only JSON goes in and out.
 *
 * Components are plain functions. Every render walks the whole tree; a
 * component's hooks live under its place in the tree (its path, with `key`
 * props where given), so its state survives re-renders.
 *
 * The bridge: a call (`query`, `action` …) is queued in `__outbox` with an id
 * and returns a promise; the host takes the queue after every step, does the
 * work and settles the promise with `__settle`.
 */
export const PRELUDE = `
"use strict";
var exports = {};
var module = { exports: exports };
var __handlers = {};
var __host = { state: null, params: null };
var __nodes = 0;
var __instances = {};
var __seen = {};
var __current = null;
var __hookIndex = 0;
var __dirty = false;
var __forced = false;
var __pendingEffects = [];
var __outbox = [];
var __calls = {};
var __nextCall = 1;
var __input = null;

function h(type, props) {
  var children = [];
  for (var i = 2; i < arguments.length; i++) children.push(arguments[i]);
  return { type: type, props: props || {}, children: children };
}
var Fragment = '__fragment';
${KIT_COMPONENTS.map((c) => `var ${c} = ${JSON.stringify(c)};`).join('\n')}

function setState(patch) { __host.state = Object.assign(__host.state || {}, patch); }
function setParams(patch) { __host.params = Object.assign(__host.params || {}, patch); }

// ---------------------------------------------------------------- hooks

function __hook() {
  if (!__current) throw new Error('Hooks (useState, useEffect …) only work inside a component while it renders.');
  var hooks = __current.hooks;
  var i = __hookIndex++;
  if (!hooks[i]) hooks[i] = {};
  return hooks[i];
}
function __depsChanged(a, b) {
  if (!a || !b || a.length !== b.length) return true;
  for (var i = 0; i < a.length; i++) if (a[i] !== b[i] && !(a[i] !== a[i] && b[i] !== b[i])) return true;
  return false;
}
function useState(initial) {
  var hook = __hook();
  if (!hook.set) {
    hook.value = typeof initial === 'function' ? initial() : initial;
    hook.set = function (next) {
      var value = typeof next === 'function' ? next(hook.value) : next;
      if (value !== hook.value) { hook.value = value; __dirty = true; }
    };
  }
  return [hook.value, hook.set];
}
function useReducer(reducer, initial) {
  var pair = useState(initial);
  var hook = __current.hooks[__hookIndex - 1];
  hook.dispatch = hook.dispatch || function (action) { hook.set(function (s) { return reducer(s, action); }); };
  return [pair[0], hook.dispatch];
}
function useMemo(make, deps) {
  var hook = __hook();
  if (!('value' in hook) || __depsChanged(hook.deps, deps)) { hook.value = make(); hook.deps = deps; }
  return hook.value;
}
function useCallback(fn, deps) { return useMemo(function () { return fn; }, deps); }
function useRef(initial) {
  var hook = __hook();
  if (!hook.ref) hook.ref = { current: initial };
  return hook.ref;
}
function useEffect(effect, deps) {
  var hook = __hook();
  if (!hook.ran || deps === undefined || __depsChanged(hook.deps, deps)) {
    hook.ran = true;
    hook.deps = deps;
    __pendingEffects.push({ hook: hook, effect: effect });
  }
}
var useLayoutEffect = useEffect;

// ---------------------------------------------------------------- the bridge to Deskworks

function __call(op, args) {
  var id = __nextCall++;
  __outbox.push({ id: id, op: op, args: args === undefined ? null : args });
  return new Promise(function (resolve, reject) { __calls[id] = { resolve: resolve, reject: reject }; });
}
function __settle(json) {
  var m = JSON.parse(json);
  var c = __calls[m.id];
  if (!c) return;
  delete __calls[m.id];
  if (m.ok) c.resolve(m.value); else c.reject(new Error(m.error || 'Failed'));
}
/** The refresh counter: goes up when the data of the workspace or its work changes. */
function __refresh() { return __input && __input.refresh || 0; }

function query(name, params) { return __call('query', { name: name, params: params || {} }); }
function useQuery(name, params) {
  var key = JSON.stringify(params || {});
  var s = useState({ rows: [], columns: [], truncated: false, loading: true, error: null });
  var tick = useState(0);
  useEffect(function () {
    var live = true;
    s[1](function (prev) { return prev.loading ? prev : Object.assign({}, prev, { loading: true }); });
    query(name, JSON.parse(key)).then(
      function (r) { if (live) s[1]({ rows: r.rows || [], columns: r.columns || [], truncated: !!r.truncated, loading: false, error: null }); },
      function (e) { if (live) s[1](function (prev) { return Object.assign({}, prev, { loading: false, error: e.message }); }); }
    );
    return function () { live = false; };
  }, [name, key, __refresh(), tick[0]]);
  return Object.assign({}, s[0], { refetch: function () { tick[1](function (n) { return n + 1; }); } });
}

function work(table, keys) { return __call('work', { table: table, keys: keys }); }
/** Work on rows: useWork('articles', id) → { status, title, approval … } or null; with an array of keys → { [key]: … }. */
function useWork(table, keys) {
  var many = Array.isArray(keys);
  var list = many ? keys.filter(function (k) { return k !== null && k !== undefined; }).map(String) : keys === null || keys === undefined ? [] : [String(keys)];
  var key = JSON.stringify(list);
  var s = useState({});
  useEffect(function () {
    var live = true;
    if (!list.length) { s[1](function (prev) { return Object.keys(prev).length ? {} : prev; }); return; }
    work(table, list).then(function (r) { if (live) s[1](r || {}); }, function () {});
    return function () { live = false; };
  }, [table, key, __refresh()]);
  return many ? s[0] : list.length ? s[0][list[0]] || null : null;
}

function action(name, row, input) { return __call('action', { name: name, row: row === undefined || row === null ? null : String(row), input: input || {} }); }
function useAction(name) {
  var s = useState({ busy: false, error: null, result: null });
  var run = useCallback(function (row, input) {
    s[1]({ busy: true, error: null, result: null });
    return action(name, row, input).then(
      function (r) { s[1]({ busy: false, error: null, result: r }); return r; },
      function (e) { s[1]({ busy: false, error: e.message, result: null }); return null; }
    );
  }, [name]);
  return Object.assign({ run: run }, s[0]);
}

function navigate(to) { __call('navigate', { to: String(to) }); }
function toast(message, tone) { __call('toast', { message: String(message), tone: tone || 'default' }); }

// ---------------------------------------------------------------- helpers

var kit = (function () {
  function key(k) { return typeof k === 'function' ? k : function (r) { return r == null ? undefined : r[k]; }; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  function toDate(v) { return v instanceof Date ? v : new Date(v); }
  var DAY = 86400000;
  return {
    groupBy: function (rows, k) {
      var f = key(k), map = {}, order = [];
      (rows || []).forEach(function (r) {
        var g = f(r); var id = String(g);
        if (!(id in map)) { map[id] = { key: g, rows: [] }; order.push(id); }
        map[id].rows.push(r);
      });
      return order.map(function (id) { var g = map[id]; return { key: g.key, rows: g.rows, count: g.rows.length }; });
    },
    count: function (rows, k) {
      return kit.groupBy(rows, k).map(function (g) { return { key: g.key, count: g.count }; });
    },
    sum: function (rows, k) { var f = key(k); return (rows || []).reduce(function (s, r) { return s + num(f(r)); }, 0); },
    avg: function (rows, k) { return rows && rows.length ? kit.sum(rows, k) / rows.length : 0; },
    min: function (rows, k) { var f = key(k); return (rows || []).reduce(function (m, r) { var v = f(r); return m === undefined || v < m ? v : m; }, undefined); },
    max: function (rows, k) { var f = key(k); return (rows || []).reduce(function (m, r) { var v = f(r); return m === undefined || v > m ? v : m; }, undefined); },
    sortBy: function (rows, k, dir) {
      var f = key(k), d = dir === 'desc' ? -1 : 1;
      return (rows || []).slice().sort(function (a, b) { var x = f(a), y = f(b); return x < y ? -d : x > y ? d : 0; });
    },
    uniq: function (values) { var seen = {}; return (values || []).filter(function (v) { var id = String(v); if (seen[id]) return false; seen[id] = true; return true; }); },
    search: function (rows, text, keys) {
      var q = String(text || '').trim().toLowerCase();
      if (!q) return rows || [];
      return (rows || []).filter(function (r) {
        var fields = keys ? keys.map(function (k) { return r[k]; }) : Object.keys(r).map(function (k) { return r[k]; });
        return fields.some(function (v) { return v !== null && v !== undefined && String(v).toLowerCase().indexOf(q) >= 0; });
      });
    },
    percent: function (part, total) { return total ? Math.round((part / total) * 1000) / 10 : 0; },
    ageDays: function (value, now) { return Math.floor(((now ? toDate(now) : new Date(__now)) - toDate(value)) / DAY); },
    ageBucket: function (value, now) {
      var d = kit.ageDays(value, now);
      return d < 1 ? '<1d' : d < 2 ? '1–2d' : d < 7 ? '2–7d' : '>7d';
    },
    day: function (value) { return toDate(value).toISOString().slice(0, 10); },
    week: function (value) {
      var d = toDate(value); var day = (d.getUTCDay() + 6) % 7;
      return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day)).toISOString().slice(0, 10);
    },
    month: function (value) { return toDate(value).toISOString().slice(0, 7); },
    formatNumber: function (n, digits) {
      var v = num(n); var f = digits == null ? (Math.abs(v) >= 100 ? 0 : 1) : digits;
      var s = v.toFixed(f); var parts = s.split('.'); parts[0] = parts[0].replace(/\\B(?=(\\d{3})+(?!\\d))/g, ',');
      return f ? parts.join('.').replace(/\\.0+$/, '') : parts[0];
    },
    formatDate: function (value) { return value == null ? '' : toDate(value).toISOString().slice(0, 10); },
    timeAgo: function (value) {
      var s = Math.round((new Date(__now) - toDate(value)) / 1000);
      if (s < 60) return 'just now'; if (s < 3600) return Math.floor(s / 60) + ' min ago';
      if (s < 86400) return Math.floor(s / 3600) + ' h ago'; return Math.floor(s / 86400) + ' d ago';
    },
  };
})();
var __now = new Date().toISOString();

// ---------------------------------------------------------------- rendering

function __childPath(path, child, index) {
  var k = child && typeof child === 'object' && child.props && child.props.key;
  return path + '/' + (k === undefined || k === null ? index : 'k' + k);
}

function __serialize(node, path) {
  if (node === null || node === undefined || node === false || node === true) return null;
  if (typeof node === 'string' || typeof node === 'number') return node;
  if (Array.isArray(node)) {
    return { type: '__fragment', props: {}, children: node.map(function (c, i) { return __serialize(c, __childPath(path, c, i)); }).filter(function (c) { return c !== null; }) };
  }
  if (typeof node !== 'object') return String(node);
  if (typeof node.type === 'function') {
    var id = path + ':' + (node.type.name || 'C');
    var inst = __instances[id];
    if (!inst || inst.fn !== node.type) { if (inst) __unmount(inst); inst = __instances[id] = { fn: node.type, hooks: [] }; }
    __seen[id] = true;
    var outer = __current, outerIndex = __hookIndex;
    __current = inst; __hookIndex = 0;
    var out;
    try { out = node.type(Object.assign({}, node.props || {}, { children: node.children || [] })); }
    finally { __current = outer; __hookIndex = outerIndex; }
    return __serialize(out, id);
  }
  if (typeof node.type !== 'string') return String(node);
  if (++__nodes > ${MAX_VIEW_NODES}) throw new Error('The view renders more than ${MAX_VIEW_NODES} elements.');
  var props = {};
  for (var k in node.props) {
    if (k === 'children') continue;
    var v = node.props[k];
    if (typeof v === 'function') { var hid = path + '#' + k; __handlers[hid] = v; props[k] = { __fn: hid }; }
    else props[k] = v;
  }
  var children = [];
  (node.children || []).forEach(function (c, i) {
    var s = __serialize(c, __childPath(path, c, i));
    if (s && s.type === '__fragment') children.push.apply(children, s.children);
    else if (s !== null) children.push(s);
  });
  return { type: node.type, props: props, children: children };
}

function __unmount(inst) {
  inst.hooks.forEach(function (hook) { if (typeof hook.cleanup === 'function') { var c = hook.cleanup; hook.cleanup = null; c(); } });
}

function __renderPass() {
  var view = module.exports.default || exports.default;
  if (typeof view !== 'function') throw new Error('The view must export a default function: export default function View({ data }) { … }');
  __handlers = {};
  __nodes = 0;
  __seen = {};
  __pendingEffects = [];
  __dirty = false;
  var tree = __serialize({ type: view, props: __input, children: [] }, 'v');
  for (var id in __instances) if (!__seen[id]) { __unmount(__instances[id]); delete __instances[id]; }
  var effects = __pendingEffects;
  __pendingEffects = [];
  effects.forEach(function (e) {
    if (typeof e.hook.cleanup === 'function') { var c = e.hook.cleanup; e.hook.cleanup = null; c(); }
    var cleanup = e.effect();
    e.hook.cleanup = typeof cleanup === 'function' ? cleanup : null;
  });
  return tree;
}

function __render(json) {
  __input = JSON.parse(json);
  __now = __input.now || __now;
  __forced = true;
}
function __event(id, json) {
  var f = __handlers[id];
  if (typeof f === 'function') f(json === undefined ? undefined : JSON.parse(json), __input);
}
/** After every step: renders again if needed (state set by an event, an effect or a settled call) and hands out what happened. */
function __flush() {
  var tree = null;
  var passes = 0;
  while (__input && (__forced || __dirty)) {
    if (++passes > 25) throw new Error('The view keeps changing its state while it renders (an effect that always sets state?).');
    __forced = false;
    tree = __renderPass();
  }
  var out = JSON.stringify({ tree: tree, effects: __host, calls: __outbox });
  __host = { state: null, params: null };
  __outbox = [];
  return out;
}
function __meta() {
  var q = module.exports.queries || exports.queries || {};
  var p = module.exports.params || exports.params || {};
  var a = module.exports.actions || exports.actions || {};
  return JSON.stringify({ queries: q, params: p, actions: a });
}
`;
//# sourceMappingURL=prelude.js.map